import type { TextAccent, TextLayer } from './template';

/**
 * Accents inside a text: parts of it in another colour, weight or with a marker, so "тариф" can
 * be yellow in a white headline.
 *
 * They come from three places, and a text a table row or the API brings works with all three:
 * - `marks`: words picked in the editor, styled wherever the text contains them;
 * - `**words**` in the text itself (a table cell, the API), drawn in the layer's accent style;
 * - numbers, prices and percentages, when the layer's `accentNumbers` is on.
 */

/** A piece of the text as drawn: plain, or with its accent style. */
export interface TextRun {
  text: string;
  style?: TextAccent;
}

/** The accent style when the layer has none: the first picked word's, or amber. */
export const DEFAULT_ACCENT: TextAccent = { color: '#F59E0B' };

const MARKUP = /\*\*([^*\n](?:[^*]*?[^*\n])?)\*\*/g;

/** The text without its `**accent**` markup (what is read, counted and measured). */
export function plainText(text: string): string {
  return text.replace(MARKUP, '$1');
}

/** The style of `**words**`, of a row's accent value and of numbers. */
export function accentStyleOf(layer: Pick<TextLayer, 'accent' | 'marks'>): TextAccent {
  if (layer.accent) return layer.accent;
  const first = layer.marks?.[0];
  if (first) {
    const { text: _text, ...style } = first;
    return style;
  }
  return DEFAULT_ACCENT;
}

// Signs, digits with thousands separators and decimals, then a percent, a plus or a currency
const NUMBER =
  /(?:[$€£₴]\s?)?[−–+-]?\d+(?:[  .,]\d{3})*(?:[.,]\d+)?(?:\s?(?:%|\+|грн|₴|zł|lei|€|\$|руб|₽|UAH|USD|EUR|PLN))?(?![\p{L}\d])/gu;

interface Range {
  start: number;
  end: number;
  style: TextAccent;
}

/** Where a fragment appears in the text: exact case first, then ignoring case. */
function occurrences(text: string, fragment: string): number[] {
  const out: number[] = [];
  const hay = text.toLocaleLowerCase();
  const needle = fragment.toLocaleLowerCase();
  // Lower-casing can change lengths (rare letters): then only exact matches are safe
  const caseless = hay.length === text.length && needle.length === fragment.length;
  const source = caseless ? hay : text;
  const target = caseless ? needle : fragment;
  if (!target.trim()) return out;
  for (let i = source.indexOf(target); i !== -1; i = source.indexOf(target, i + target.length))
    out.push(i);
  return out;
}

/** The text cut into plain and accented runs, in order. */
export function textRuns(
  layer: Pick<TextLayer, 'text' | 'marks' | 'accent' | 'accentNumbers'>,
  /** false: the `**markup**` stays as typed (the text being edited keeps its asterisks). */
  opts: { markup?: boolean } = {},
): TextRun[] {
  const accent = accentStyleOf(layer);
  // 1. `**markup**`: taken out of the text, its words in the accent style
  const ranges: Range[] = [];
  let plain = '';
  let last = 0;
  for (const m of opts.markup === false ? [] : layer.text.matchAll(MARKUP)) {
    plain += layer.text.slice(last, m.index);
    ranges.push({ start: plain.length, end: plain.length + m[1].length, style: accent });
    plain += m[1];
    last = m.index! + m[0].length;
  }
  plain += layer.text.slice(last);
  const free = (start: number, end: number) => !ranges.some((r) => start < r.end && end > r.start);
  // 2. Words picked in the editor, wherever they are
  for (const mark of layer.marks ?? []) {
    const { text: fragment, ...style } = mark;
    for (const at of occurrences(plain, fragment)) {
      const end = at + fragment.length;
      if (free(at, end)) ranges.push({ start: at, end, style });
    }
  }
  // 3. Numbers
  if (layer.accentNumbers)
    for (const m of plain.matchAll(NUMBER)) {
      const end = m.index! + m[0].length;
      if (/\d/.test(m[0]) && free(m.index!, end))
        ranges.push({ start: m.index!, end, style: accent });
    }
  if (!ranges.length) return [{ text: plain }];
  ranges.sort((a, b) => a.start - b.start);
  const runs: TextRun[] = [];
  let at = 0;
  for (const r of ranges) {
    if (r.start > at) runs.push({ text: plain.slice(at, r.start) });
    runs.push({ text: plain.slice(r.start, r.end), style: r.style });
    at = r.end;
  }
  if (at < plain.length) runs.push({ text: plain.slice(at) });
  return runs;
}

/** Whether the layer draws any accent (so a plain text renders as before). */
export function hasAccents(layer: Pick<TextLayer, 'text' | 'marks' | 'accentNumbers'>): boolean {
  return Boolean(layer.marks?.length || layer.accentNumbers || layer.text.includes('**'));
}
